import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import lotusLogo from "@/assets/lotus-bakeries-logo.png";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { LogOut, ArrowLeft, LayoutDashboard, Settings2 } from "lucide-react";

interface AppHeaderProps {
  title?: string;
  showBack?: boolean;
  backPath?: string;
  onBack?: () => void;
}

export function AppHeader({ title, showBack = false, backPath, onBack }: AppHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const isOnTesterView = location.pathname.startsWith("/tester");

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
    } finally {
      navigate("/auth", { replace: true });
      setIsLoggingOut(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  return (
    <header className="w-full py-4 px-6 border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-4">
          {showBack && (
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <img
            src={lotusLogo}
            alt="Lotus Bakeries"
            className="h-8 md:h-9 object-contain"
          />
          {title && (
            <>
              <div className="h-6 w-px bg-border hidden md:block" />
              <h1 className="text-lg font-display font-semibold hidden md:block">{title}</h1>
            </>
          )}
        </div>

        <div className="flex items-center gap-4">
          {user && (
            <>
              <div className="hidden md:flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{user.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  isAdmin 
                    ? 'bg-primary/10 text-primary' 
                    : 'bg-info/10 text-info'
                }`}>
                  {isAdmin ? (isOnTesterView ? 'Admin (Tester view)' : 'Admin') : 'Tester'}
                </span>
              </div>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(isOnTesterView ? "/admin/projects" : "/tester/dashboard")}
                  className="gap-2"
                  title={isOnTesterView ? "Switch to Admin view" : "Switch to Tester view"}
                >
                  {isOnTesterView ? (
                    <><Settings2 className="h-4 w-4" /><span className="hidden md:inline">Admin view</span></>
                  ) : (
                    <><LayoutDashboard className="h-4 w-4" /><span className="hidden md:inline">Tester view</span></>
                  )}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="gap-2"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden md:inline">Sign out</span>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
